/* V167 — Manifest A/B Test Admin Tab
 *
 * Tracks impressions + conversions per variant, current week + lifetime.
 * Highlights winning variant when statistical sample is reasonable (>=100 impressions).
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VARIANT_LABELS = {
  A: 'A — "SEO nástroje sú mŕtve"',
  B: 'B — "Prestaň kupovať dashboardy"',
  C: 'C — "Tvoj e-shop nepotrebuje ďalší nástroj"',
};

function findWinner(rows) {
  const eligible = rows.filter((r) => r.impressions >= 100);
  if (!eligible.length) return null;
  return eligible.reduce((best, r) => (r.cvr_pct > best.cvr_pct ? r : best), eligible[0]);
}

function VariantRow({ row, winner }) {
  const isWinner = winner && winner.variant === row.variant;
  return (
    <div className={`grid grid-cols-12 items-center gap-3 px-4 py-3 border-b border-zinc-900 ${isWinner ? "bg-emerald-500/5" : ""}`} data-testid={`variant-row-${row.variant}`}>
      <div className="col-span-12 sm:col-span-5">
        <div className="text-sm font-bold text-white">{VARIANT_LABELS[row.variant] || row.variant}</div>
        <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{row.name}</div>
      </div>
      <div className="col-span-4 sm:col-span-2 text-zinc-300 text-sm font-mono">{row.impressions.toLocaleString("sk-SK")}</div>
      <div className="col-span-4 sm:col-span-2 text-zinc-300 text-sm font-mono">{row.conversions.toLocaleString("sk-SK")}</div>
      <div className="col-span-4 sm:col-span-3 flex items-center gap-2">
        <div className={`text-base font-black ${isWinner ? "text-emerald-300" : "text-white"}`}>{row.cvr_pct}%</div>
        {isWinner && <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5">winner</span>}
      </div>
    </div>
  );
}

export default function AdminManifestABTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = () => {
    const token = localStorage.getItem("admin_token") || "";
    if (!token) { setErr("Admin token chýba."); return; }
    axios.get(`${API}/manifest/admin/stats`, { headers: { "X-Admin-Token": token } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "load_failed"));
  };
  useEffect(() => { load(); }, []);

  if (err) return <div className="p-6 text-rose-300" data-testid="manifest-ab-error">{err}</div>;
  if (!data) return <div className="p-6 text-zinc-500" data-testid="manifest-ab-loading">Načítavam A/B dáta…</div>;

  const lifetimeWinner = findWinner(data.lifetime || []);

  return (
    <div className="p-3 sm:p-6 space-y-6" data-testid="admin-manifest-ab-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-white">🧪 Manifest A/B Test</h2>
          <p className="text-zinc-400 text-sm mt-1">V167 — Cold-traffic landing /manifest · current week: <span className="font-mono text-emerald-300">{data.current_week}</span></p>
        </div>
        <button data-testid="manifest-ab-refresh" onClick={load} className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs rounded hover:bg-zinc-800">↻ Obnoviť</button>
      </div>

      {/* Lifetime */}
      <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-sm font-bold text-zinc-200">Lifetime totals</h3>
          {lifetimeWinner && (
            <span className="text-xs text-emerald-300 font-mono">
              Vyhráva variant {lifetimeWinner.variant} ({lifetimeWinner.cvr_pct}% CVR)
            </span>
          )}
        </div>
        <div className="grid grid-cols-12 items-center gap-3 px-4 py-2 bg-zinc-900 text-[10px] text-zinc-500 font-mono uppercase tracking-wider">
          <div className="col-span-12 sm:col-span-5">Variant</div>
          <div className="col-span-4 sm:col-span-2">Impressions</div>
          <div className="col-span-4 sm:col-span-2">Conversions</div>
          <div className="col-span-4 sm:col-span-3">CVR</div>
        </div>
        {(data.lifetime || []).map((row) => (
          <VariantRow key={row.variant} row={row} winner={lifetimeWinner}/>
        ))}
      </div>

      {/* Per week */}
      {(data.weeks || []).map((wk) => {
        const wkWinner = findWinner(wk.variants);
        return (
          <div key={wk.week} className="bg-zinc-950/60 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-sm font-bold text-zinc-200">Týždeň {wk.week}</h3>
              {wkWinner && <span className="text-xs text-emerald-300 font-mono">Winner: {wkWinner.variant}</span>}
            </div>
            {wk.variants.map((row) => (
              <VariantRow key={row.variant} row={row} winner={wkWinner}/>
            ))}
          </div>
        );
      })}
      {(!data.weeks || data.weeks.length === 0) && (
        <div className="text-zinc-500 text-sm">Žiadne dáta zatiaľ. Spusti FB reklamu na /manifest a počkaj na prvé impresie.</div>
      )}
    </div>
  );
}
